import { Trans, useLingui } from "@lingui/macro";
import { Check, Sparkles } from "lucide-react";
import type { CorrectionErrorClass } from "@app/types/correction";
import {
  IN_RECORD_ORDER,
  NOT_IN_RECORD_ORDER,
  errorClassOption,
} from "@app/utils/correction-classes";

/**
 * The closed question that replaced two free-text boxes.
 *
 * The doctor does not write a justification any more: they arbitrate a class the machine
 * has already proposed, or narrowed. That inversion is the design — what was redundant to
 * type ("écrit textuellement dans le texte") is a click, and what actually carries
 * information keeps asking for words.
 *
 * The two headings are not decoration. "The corrected value is somewhere in this record"
 * is a fact the backend can often check, and it eliminates three of the eight without
 * choosing among the rest. Rendering the groups is what lets a *narrowing* be shown as a
 * narrowing instead of being thrown away for not being a single answer.
 *
 * NOTHING IS DIMMED BY DEFAULT, and that is a rule rather than a setting. `candidates`
 * arrives empty unless a POSITIVE find — the value located in an actual passage, about the
 * value the doctor actually typed — eliminated something. Two dimmings were removed for
 * failing that test: one narrowed whenever the value was *not* found among the record's
 * newest 40 passages, which fired on 284 of 300 real corrections and greyed out the class
 * doctors pick most often; the other greyed the four classes that presuppose a corrected
 * value, before the doctor had done anything at all. A constraint that has not yet applied
 * is said in words when it applies, not by switching off half the list on arrival.
 */

interface CorrectionErrorClassPickerProps {
  value: CorrectionErrorClass | null;
  onChange: (value: CorrectionErrorClass) => void;
  /** The machine's single proposal, when it has one. */
  suggested: CorrectionErrorClass | null;
  /** The subset the evidence leaves open. Empty means no narrowing. */
  candidates?: CorrectionErrorClass[];
  /** One sentence explaining what the machine went on. */
  rationale?: string;
}

function ClassButton({
  option,
  isSelected,
  isSuggested,
  isDimmed,
  onSelect,
  label,
  hint,
}: {
  option: CorrectionErrorClass;
  isSelected: boolean;
  isSuggested: boolean;
  isDimmed: boolean;
  onSelect: () => void;
  label: string;
  hint: string;
}) {
  return (
    <button
      key={option}
      type="button"
      onClick={onSelect}
      aria-pressed={isSelected}
      className={[
        "w-full rounded-lg border px-3 py-2 text-left transition-colors",
        isSelected
          ? "border-primary bg-primary/5"
          : "border-slate-200 hover:border-primary/50 hover:bg-slate-50",
        // Eliminated by the evidence, never removed: the machine narrows, the doctor
        // decides. Hiding a class would make a wrong narrowing unrecoverable without
        // reopening the modal.
        isDimmed && !isSelected ? "opacity-50" : "",
      ].join(" ")}
    >
      <span className="flex items-start gap-2">
        <span
          className={[
            "mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border",
            isSelected ? "border-primary bg-primary text-white" : "border-slate-300",
          ].join(" ")}
        >
          {isSelected && <Check className="h-3 w-3" />}
        </span>
        <span className="min-w-0">
          <span className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-slate-900">{label}</span>
            {isSuggested && !isSelected && (
              <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[11px] text-blue-900">
                <Trans>Proposé</Trans>
              </span>
            )}
          </span>
          <span className="mt-0.5 block text-xs text-slate-500">{hint}</span>
        </span>
      </span>
    </button>
  );
}

export default function CorrectionErrorClassPicker({
  value,
  onChange,
  suggested,
  candidates = [],
  rationale,
}: CorrectionErrorClassPickerProps) {
  const { t } = useLingui();
  const narrowed = candidates.length > 0;
  // « Pré-rempli » ne vaut que si la proposition est EFFECTIVEMENT retenue. Sur une valeur
  // inchangée elle ne l'est jamais — une classe y vaut signalement — et l'annoncer
  // pré-remplie ferait chercher au médecin une sélection qui n'existe pas.
  const prefilled = suggested !== null && value === suggested;
  const isDimmed = (option: CorrectionErrorClass) =>
    narrowed && !candidates.includes(option);

  const renderGroup = (
    heading: string,
    options: CorrectionErrorClass[],
  ) => (
    <div className="grid gap-1.5">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
        {heading}
      </span>
      {options.map((option) => {
        const descriptor = errorClassOption(option);
        return (
          <ClassButton
            key={option}
            option={option}
            isSelected={value === option}
            isSuggested={suggested === option}
            isDimmed={isDimmed(option)}
            onSelect={() => onChange(option)}
            label={t(descriptor.label)}
            hint={t(descriptor.hint)}
          />
        );
      })}
    </div>
  );

  return (
    <div className="grid gap-3">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-sm font-semibold text-slate-900">
          <Trans>Pourquoi la valeur était fausse ?</Trans>
        </span>
        {(prefilled || narrowed) && (
          <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-900">
            <Sparkles className="h-3 w-3" />
            {prefilled ? <Trans>Pré-rempli</Trans> : <Trans>Restreint</Trans>}
          </span>
        )}
      </div>

      {/* The rationale is shown whenever the machine narrowed or proposed. A default
          nobody can inspect gets accepted blindly, which would turn the pre-fill into a
          machine for manufacturing labels that read as arbitrated by a clinician. */}
      {(suggested || narrowed) && rationale && (
        <p className="rounded-lg border border-primary/20 bg-blue-50 px-3 py-2 text-xs text-blue-900">
          {rationale}
        </p>
      )}

      {renderGroup(t`L'information est dans le dossier`, IN_RECORD_ORDER)}
      {renderGroup(t`Elle n'y est pas`, NOT_IN_RECORD_ORDER)}
    </div>
  );
}
